"use client";

/**
 * FileTileCaption — the caption row of the unified file grammar (founder
 * pick 08-02, option A amended): a small colored type glyph, then the
 * name. Nothing else — the old meta sub-line is gone; task-of-origin and
 * dates live in the file preview. Shared by every folder view (Creations,
 * Uploaded, Temp, Media, user folders) so a file reads identically
 * everywhere.
 */

import { cn } from "@/lib/utils";
import { FileKindGlyph } from "@/components/icons/FileKindGlyph";

export type FileKind =
  | "report"
  | "page"
  | "doc"
  | "image"
  | "video"
  | "audio"
  | "table"
  | "code"
  | "other";

export const KIND_COLOR: Record<FileKind, string> = {
  report: "text-[#b4552f]",
  page: "text-[#4f46b8]",
  doc: "text-[#3f6da8]",
  image: "text-[#3e7d4c]",
  video: "text-[#7c5cd6]",
  audio: "text-[#6a51b8]",
  table: "text-[#2f7d68]",
  code: "text-[#b0447c]",
  other: "text-fg-subtle",
};

/** Derive the caption kind from a filename's extension (user files). */
export function kindFromFilename(filename: string): FileKind {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  if (["png", "jpg", "jpeg", "gif", "webp", "svg", "heic"].includes(ext))
    return "image";
  if (["mp4", "webm", "mov"].includes(ext)) return "video";
  if (["mp3", "wav", "ogg", "m4a"].includes(ext)) return "audio";
  if (["csv", "xlsx", "xls"].includes(ext)) return "table";
  if (["html", "htm"].includes(ext)) return "page";
  if (
    [
      "py",
      "js",
      "ts",
      "tsx",
      "jsx",
      "sh",
      "sql",
      "css",
      "json",
      "yaml",
      "yml",
      "xml",
    ].includes(ext)
  )
    return "code";
  if (
    ["pdf", "doc", "docx", "md", "mdx", "markdown", "txt", "rtf"].includes(ext)
  )
    return "doc";
  return "other";
}

/** Derive the caption kind from a creation artifact (kind + subkind). */
export function kindFromArtifact(a: {
  kind: string;
  subkind?: string | null;
  filename?: string | null;
}): FileKind {
  if (a.subkind === "report") return "report";
  if (a.subkind === "page") return "page";
  if (a.kind === "image") return "image";
  if (a.kind === "video") return "video";
  if (a.kind === "audio") return "audio";
  return kindFromFilename(a.filename ?? "");
}

export function FileTileCaption({
  kind,
  name,
  title,
  selected,
}: {
  kind: FileKind;
  name: string;
  title?: string;
  selected?: boolean;
}) {
  return (
    <span
      className={cn(
        "mt-1.5 flex w-fit max-w-full items-center justify-center gap-1.5 min-w-0 px-1.5 py-0.5 rounded",
        selected
          ? "bg-palette-blue text-fg-inverse font-medium"
          : "text-fg-default font-medium group-data-[state=open]/tile:bg-palette-blue group-data-[state=open]/tile:text-fg-inverse"
      )}
      title={title ?? name}
    >
      <FileKindGlyph
        kind={kind}
        className={
          selected
            ? "text-current"
            : "group-data-[state=open]/tile:text-current"
        }
      />
      <span className="text-3 truncate min-w-0 leading-tight">{name}</span>
    </span>
  );
}
